import { Button, Chip, MenuItem, Select, Tooltip } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { GridColDef } from "@mui/x-data-grid";
import { PaymentMethod } from "../../Fleet/PaymentMethod";
import { OnHoldRepairStatus, RepairStatus } from "../../Fleet/RepairStatus";
import DownloadButton from "../../../components/Fleet/DownloadButton/DownloadButton";
import ShopNameCell from "../../../components/Fleet/ShopNameCell/ShopNameCell";
import { IShopDetails } from "../../../interfaces/Fleet/IShopDetails";
import { formatDate } from "../../../helpers/formatingFunctions";
import ConfirmActionButton from "../../../components/Fleet/ConfirmActionButton/ConfirmActionButton";

export type RepairStatusType =
  | "notStarted"
  | "pending"
  | "completed"
  | "waitingForInvoice"
  | "onHold";

export const statusColors: Record<
  RepairStatusType,
  {
    label: string;
    color:
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning";
  }
> = {
  notStarted: { label: "Not Started", color: "default" },
  pending: { label: "Pending", color: "warning" },
  completed: { label: "Completed", color: "success" },
  onHold: { label: "On Hold", color: "info" },
  waitingForInvoice: { label: "Waiting For Invoice", color: "info" },
};

export const createRepairsTableColumns = (
  handleClick: (details: IShopDetails) => void,
  handleGenerateAiSplits?: (repair: any) => void,
): GridColDef[] => [
  {
    field: "aiSplits",
    headerName: "AI Splits",
    width: 190,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <Button
        size="small"
        variant="outlined"
        startIcon={<AutoAwesomeIcon />}
        onClick={() => handleGenerateAiSplits?.(params.row)}
      >
        Generate Splits
      </Button>
    ),
  },
  {
    field: "shopName",
    headerName: "Shop Name",
    width: 200,
    renderCell: (params) => (
      <ShopNameCell {...params} handleClick={handleClick} />
    ),
  },
  {
    field: "estimatedCost",
    headerName: "Estimated Cost",
    width: 140,
    editable: true,
    valueFormatter: (value) => (value ? `$${value}` : ""),
  },
  {
    field: "paymentMethod",
    headerName: "Payment Method",
    width: 150,
    editable: true,
    renderEditCell: (params) => (
      <Select
        onChange={(event) =>
          params.api.setEditCellValue(
            { id: params.id, field: params.field, value: event.target.value },
            event,
          )
        }
        defaultValue={
          PaymentMethod.find(
            (option) =>
              option.value.toLowerCase() ===
              String(params.value ?? "").toLowerCase(),
          )?.value ?? PaymentMethod[0].value
        }
        fullWidth
      >
        {PaymentMethod.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    ),
    valueFormatter: (value) => {
      if (value === "lovesNationalAccount") {
        return "Love's National Account";
      } else if (value === "taNationalAccount") {
        return "TA National Account";
      } else if (value === "michelinNationalAccount") {
        return "Michelin National Account";
      } else if (value === "bridgestoneNationalAccount") {
        return "Bridgestone National Account";
      } else if (value === "continentalNationalAccount") {
        return "Continental National Account";
      } else if (value === "pompsNationalAccount") {
        return "Pomp's National Account";
      } else if (value === "southernTireMartNationalAccount") {
        return "Southern Tire Mart National Account";
      } else if (value === "creditCard") {
        return "Credit Card";
      } else if (value === "cash") {
        return "Cash";
      } else if (value === "efs") {
        return "EFS";
      } else if (value === "warranty") {
        return "Warranty";
      } else if (value === "paidByDriver") {
        return "Paid By Driver";
      } else if (value === "dealerAccount") {
        return "Dealer Account";
      } else if (value === "other") {
        return "Other";
      }
    },
  },
  {
    field: "authorizedBy",
    headerName: "Authorized By",
    width: 140,
    editable: true,
  },
  {
    field: "repairStatus",
    headerName: "Repair Status",
    width: 130,
    editable: true,
    renderCell: (params) => (
      <Tooltip
        title={
          params.value === "completed" && params.row.completedBy
            ? `Completed by ${params.row.completedBy} on ${new Date(
                params.row.completedDate,
              ).toLocaleDateString()}`
            : params.value === "onHold" && params.row.onHoldReason
              ? `On Hold Reason: ${params.row.onHoldReason}`
              : ""
        }
      >
        <Chip
          label={statusColors[params.value as RepairStatusType].label}
          color={statusColors[params.value as RepairStatusType].color}
        />
      </Tooltip>
    ),
    renderEditCell: (params) => (
      <Select
        onChange={(event) =>
          params.api.setEditCellValue(
            { id: params.id, field: params.field, value: event.target.value },
            event,
          )
        }
        defaultValue={RepairStatus[0].value}
        fullWidth
      >
        {params.row.repairStatus === "onHold"
          ? OnHoldRepairStatus.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))
          : RepairStatus.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
      </Select>
    ),
    valueFormatter: (value) => {
      if (value === "pending") {
        return "Pending";
      } else if (value === "completed") {
        return "Completed";
      } else if (value === "onHold") {
        return "On Hold";
      } else if (value === "notStarted") {
        return "Not Started";
      } else if (value === "waitingForInvoice") {
        return "Waiting For Invoice";
      }
    },
  },
  {
    field: "finalCost",
    headerName: "Final Cost",
    width: 130,
    editable: true,
    valueFormatter: (value) => (value ? `$${value}` : ""),
  },
  {
    field: "invoiceDate",
    headerName: "Invoice Date",
    width: 150,
    editable: true,
    valueFormatter: (value) => value && formatDate(value),
    renderEditCell: (params) => (
      <input
        type="date"
        value={params.value}
        onChange={(event) =>
          params.api.setEditCellValue(
            { id: params.id, field: params.field, value: event.target.value },
            event,
          )
        }
      />
    ),
  },
  {
    field: "invoiceNumber",
    headerName: "Invoice Number",
    width: 150,
    editable: true,
    renderEditCell(params) {
      return (
        <input
          type="text"
          value={params.value}
          onChange={(event) =>
            params.api.setEditCellValue(
              { id: params.id, field: params.field, value: event.target.value },
              event,
            )
          }
        />
      );
    },
  },
  {
    field: "downloadInvoice",
    headerName: "Download Invoice",
    width: 194,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) =>
      (params.row.filesAvailability === "invoice" ||
        params.row.filesAvailability === "both") && (
        <DownloadButton params={params} type="invoice" />
      ),
  },
  {
    field: "downloadReceipt",
    headerName: "Download Receipt",
    width: 196,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) =>
      (params.row.filesAvailability === "receipt" ||
        params.row.filesAvailability === "both") && (
        <DownloadButton params={params} type="receipt" />
      ),
  },
  {
    field: "delete",
    headerName: "",
    width: 130,
    renderCell: (params) => (
      <ConfirmActionButton
        id={params.row.id}
        entityType="Repair"
        actionType="delete"
        entityLabel="repair"
        queryKey="repairs"
        buttonLabel="Delete"
        buttonColor="error"
      />
    ),
  },
];
